import type { NodeModel } from "@/models/nodes/types";
import type { TailwindColor } from "@/utils/tailwindColors";
import type { OperatorSemanticType } from "@corca/data";
import type { MessageKeys, NestedKeyOf } from "next-intl";

export const SHADES = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
  "950",
] as const;

export const NAMED_COLOR = ["white", "black", "transparent"] as const;

export type NamedColor = (typeof NAMED_COLOR)[number];

export type Shade = (typeof SHADES)[number];

export type Color = `${TailwindColor}-${Shade}` | NamedColor;

type OperatorSymbolTypes = "default" | Extract<OperatorSemanticType, "relation" | "punctuation">;

export type ThemedNodeModel =
  | NodeModel<"number">
  | NodeModel<"variable">
  | NodeModel<"constant">
  | NodeModel<"function">
  | NodeModel<"operatorSymbol">;

type FixedColor = {
  type: "fixed";
  color: Color;
};

type ColorShade = {
  type: "colored";
  shade: Shade;
};

export type ColorTypeSelect = FixedColor | ColorShade;

type OptionalFixedColor =
  | (FixedColor & { enabled: true })
  | (Partial<FixedColor> & { enabled: false; type: FixedColor["type"] });

export type OptionalColorTypeSelect =
  | (ColorTypeSelect & { enabled: true })
  | (Partial<ColorTypeSelect> & { enabled: false; type: ColorTypeSelect["type"] });

type NumberNodeTheme = {
  symbol: FixedColor;
  stroke: OptionalFixedColor;
  background: OptionalFixedColor;
};

type VariableNodeTheme = {
  symbol: ColorTypeSelect;
  stroke: OptionalColorTypeSelect;
  background: OptionalColorTypeSelect;
};

type ConstantNodeTheme = {
  symbol: ColorTypeSelect;
  stroke: OptionalColorTypeSelect;
  background: OptionalColorTypeSelect;
};

type FunctionNodeTheme = {
  symbol: FixedColor;
  stroke: OptionalFixedColor;
  background: OptionalFixedColor;
};

type OperatorSymbolNodeTheme = {
  symbol: FixedColor;
  stroke: OptionalFixedColor;
  background: OptionalFixedColor;
};

type NodeThemeConfigMap = {
  number: NumberNodeTheme;
  variable: VariableNodeTheme;
  constant: ConstantNodeTheme;
  function: FunctionNodeTheme;
  operatorSymbol: Record<OperatorSymbolTypes, OperatorSymbolNodeTheme>;
};

export type NodesThemeConfig = {
  [K in ThemedNodeModel["type"]]: NodeThemeConfigMap[K];
};

export type NodeTypeToTheme<T extends NodeModel> = T["type"] extends keyof NodesThemeConfig
  ? T["type"] extends "operatorSymbol"
    ? NodesThemeConfig[T["type"]][OperatorSymbolTypes]
    : NodesThemeConfig[T["type"]]
  : never;

export type ThemeIntlName = MessageKeys<
  IntlMessages["mathTheme"],
  NestedKeyOf<IntlMessages["mathTheme"]>
>;

export type NodeTheme = {
  id: string;
  intlName?: ThemeIntlName;
  sidebarGroupKey?: string;
  nodes: NodesThemeConfig;
};
